import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { adminApi } from '../../services/api'

export interface AdminUser {
  id: string
  uuid?: string
  email: string
  name: string
  role: 'super_admin' | 'support_admin' | 'content_admin' | 'finance_admin'
  isSuspended: boolean
  lastLoginAt: Date | null
  loginAttempts?: number
  passwordChangedAt?: Date
  createdAt: Date
  updatedAt: Date
  department?: string
  phoneNumber?: string
  twoFactorEnabled?: boolean
}

export interface AdminUserListItem {
  id: string
  uuid: string
  name: string
  email: string
  role: string
  isSuspended: boolean
  lastLoginAt: string | null
  createdAt: string
  updatedAt: string
}

export interface AuditLogEntry {
  id: string
  adminPerformerId: number
  adminPerformerName: string
  targetAdminId: number
  targetAdminName: string
  action: string
  oldValues: any
  newValues: any
  ipAddress: string
  userAgent: string
  timestamp: string
}

interface AdminUsersState {
  admins: AdminUserListItem[]
  selectedAdmin: AdminUser | null
  auditLogs: AuditLogEntry[]
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  filters: {
    search: string
    role: string
    status: 'active' | 'suspended' | 'all'
    sortBy: string
    sortOrder: 'asc' | 'desc'
  }
}

const initialState: AdminUsersState = {
  admins: [],
  selectedAdmin: null,
  auditLogs: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    role: 'all',
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
}

// Async thunks for API operations
export const fetchAdminUsers = createAsyncThunk(
  'adminUsers/fetchAll',
  async (params?: {
    page?: number
    limit?: number
    search?: string
    role?: string
    status?: 'active' | 'suspended' | 'all'
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }) => {
    const response = await adminApi.listAdmins(params)
    return response.data
  }
)

export const createAdminUser = createAsyncThunk(
  'adminUsers/create',
  async (data: {
    name: string
    email: string
    role: string
    password: string
    sendWelcomeEmail?: boolean
  }) => {
    const response = await adminApi.createAdmin(data)
    return response.data
  }
)

export const fetchAdminDetails = createAsyncThunk(
  'adminUsers/fetchDetails',
  async (adminId: string) => {
    const response = await adminApi.getAdminDetails(adminId)
    return response.data
  }
)

export const updateAdminUser = createAsyncThunk(
  'adminUsers/update',
  async ({ adminId, data }: {
    adminId: string
    data: {
      name?: string
      email?: string
      role?: string
      isSuspended?: boolean
    }
  }) => {
    const response = await adminApi.updateAdmin(adminId, data)
    return response.data
  }
)

export const deleteAdminUser = createAsyncThunk(
  'adminUsers/delete',
  async (adminId: string) => {
    const response = await adminApi.deleteAdmin(adminId)
    return { adminId, ...response.data }
  }
)

export const resetAdminPassword = createAsyncThunk(
  'adminUsers/resetPassword',
  async ({ adminId, data }: {
    adminId: string
    data: {
      sendEmail?: boolean
      temporaryPassword?: string
      forceChangeOnLogin?: boolean
    }
  }) => {
    const response = await adminApi.resetAdminPassword(adminId, data)
    return response.data
  }
)

export const suspendAdminUser = createAsyncThunk(
  'adminUsers/suspend',
  async (adminId: string) => {
    const response = await adminApi.suspendAdmin(adminId)
    return { adminId, ...response.data }
  }
)

export const unsuspendAdminUser = createAsyncThunk(
  'adminUsers/unsuspend',
  async (adminId: string) => {
    const response = await adminApi.unsuspendAdmin(adminId)
    return { adminId, ...response.data }
  }
)

export const fetchAdminAuditLogs = createAsyncThunk(
  'adminUsers/fetchAuditLogs',
  async ({ adminId, params }: {
    adminId: string
    params?: {
      page?: number
      limit?: number
      action?: string
      dateFrom?: string
      dateTo?: string
    }
  }) => {
    const response = await adminApi.getAdminAuditLogs(adminId, params)
    return response.data
  }
)

const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<AdminUsersState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearSelectedAdmin: (state) => {
      state.selectedAdmin = null
      state.auditLogs = []
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch admin users
      .addCase(fetchAdminUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.loading = false
        state.admins = action.payload.data || []
        state.pagination = action.payload.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
        }
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch admin users'
      })
      
      // Create admin user
      .addCase(createAdminUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createAdminUser.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.data) {
          state.admins.unshift(action.payload.data)
          state.pagination.total += 1
        }
      })
      .addCase(createAdminUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create admin user'
      })
      
      // Fetch admin details
      .addCase(fetchAdminDetails.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdminDetails.fulfilled, (state, action) => {
        state.loading = false
        state.selectedAdmin = action.payload.data
      })
      .addCase(fetchAdminDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch admin details'
      })
      
      // Update admin user
      .addCase(updateAdminUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateAdminUser.fulfilled, (state, action) => {
        state.loading = false
        const updatedAdmin = action.payload.data
        if (updatedAdmin) {
          const index = state.admins.findIndex(a => a.id === updatedAdmin.id)
          if (index !== -1) {
            state.admins[index] = updatedAdmin
          }
          if (state.selectedAdmin?.id === updatedAdmin.id) {
            state.selectedAdmin = { ...state.selectedAdmin, ...updatedAdmin }
          }
        }
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update admin user'
      })
      
      // Delete admin user
      .addCase(deleteAdminUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.loading = false
        state.admins = state.admins.filter(a => a.id !== action.payload.adminId)
        state.pagination.total -= 1
        if (state.selectedAdmin?.id === action.payload.adminId) {
          state.selectedAdmin = null
        }
      })
      .addCase(deleteAdminUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete admin user'
      })
      
      // Reset password
      .addCase(resetAdminPassword.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(resetAdminPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to reset password'
      })
      
      // Suspend admin
      .addCase(suspendAdminUser.fulfilled, (state, action) => {
        const admin = state.admins.find(a => a.id === action.payload.adminId)
        if (admin) {
          admin.isSuspended = true
        }
        if (state.selectedAdmin?.id === action.payload.adminId) {
          state.selectedAdmin.isSuspended = true
        }
      })
      
      // Unsuspend admin
      .addCase(unsuspendAdminUser.fulfilled, (state, action) => {
        const admin = state.admins.find(a => a.id === action.payload.adminId)
        if (admin) {
          admin.isSuspended = false
        }
        if (state.selectedAdmin?.id === action.payload.adminId) {
          state.selectedAdmin.isSuspended = false
        }
      })
      
      // Fetch audit logs
      .addCase(fetchAdminAuditLogs.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdminAuditLogs.fulfilled, (state, action) => {
        state.loading = false
        state.auditLogs = action.payload.data || []
      })
      .addCase(fetchAdminAuditLogs.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch audit logs'
      })
  },
})

export const { setFilters, clearSelectedAdmin, clearError } = adminUsersSlice.actions
export default adminUsersSlice.reducer